if (!customElements.get('osm-map')) {
	// OpenFreeMap vector styles (free, no API key) for each "Style" setting value
	const OSM_MAP_STYLES = {
		default: 'https://tiles.openfreemap.org/styles/liberty',
		grayscale: 'https://tiles.openfreemap.org/styles/positron',
		night: 'https://tiles.openfreemap.org/styles/dark'
	};

	class OsmMap extends HTMLElement {
		constructor() {
			super();
			this.container = this.querySelector('.osm-map--map');
			this.mapZoom = parseInt(this.container.dataset.mapZoom, 10);
			this.language = (this.container.dataset.language || 'en').split('-')[0].toLowerCase();
			const configElement = this.querySelector('[data-osm-map-config]');
			this.config = configElement ? JSON.parse(configElement.textContent) : { locations: [] };
			this.location_informations = Array.from(this.querySelectorAll('collapsible-row'));
			this.map = false;
		}
		connectedCallback() {
			if (!window.maplibregl || this.map) {
				return;
			}
			this.renderMap();

			this.location_informations.forEach((information) => {
				information.querySelector('summary').addEventListener('click', () => {
					this.closeOthers(information);
					const location = this.config.locations.find((loc) => loc.handle === information.dataset.handle);
					if (location) {
						this.scrollToPin(location);
					}
				});
			});
		}
		disconnectedCallback() {
			if (this.map) {
				this.map.remove();
				this.map = false;
			}
		}
		renderMap() {
			const isTouch = window.matchMedia('(pointer: coarse)').matches;

			this.map = new maplibregl.Map({
				container: this.container,
				style: OSM_MAP_STYLES[this.container.dataset.mapStyle] || OSM_MAP_STYLES.default,
				center: [0, 20],
				zoom: 1,
				scrollZoom: this.container.dataset.scrollZoom === 'true',
				dragPan: !isTouch,
				dragRotate: false,
				pitchWithRotate: false,
				touchPitch: false,
				attributionControl: false
			});
			this.map.touchZoomRotate.disableRotation();
			// Data credits (OpenFreeMap, OpenMapTiles, OpenStreetMap) only, without the MapLibre link
			this.map.addControl(new maplibregl.AttributionControl({ compact: true }));

			// Show place names in the shopper's language once the style has loaded
			this.map.on('style.load', () => this.localizeLabels());

			this.bounds = new maplibregl.LngLatBounds();
			this.config.locations.forEach((location) => {
				this.renderMarker(location);
			});

			this.fitMap();
		}
		localizeLabels() {
			// Name in the shop language, then the latin-script name, then the local name
			const textField = ['coalesce', ['get', `name:${this.language}`], ['get', 'name:latin'], ['get', 'name']];

			this.map.getStyle().layers.forEach((layer) => {
				const current = layer.layout && layer.layout['text-field'];
				// Only touch name labels, not road numbers or house numbers
				if (current && JSON.stringify(current).includes('name')) {
					this.map.setLayoutProperty(layer.id, 'text-field', textField);
				}
			});
		}
		pinSize() {
			const [imageWidth, imageHeight] = this.config.marker_size || [79, 120];
			const width = this.config.pin_size || 32;
			return [width, Math.round(width * imageHeight / imageWidth)];
		}
		fitMap() {
			if (this.bounds.isEmpty()) {
				return;
			}
			if (this.mapZoom > 0) {
				// MapLibre zoom levels are one step below the old raster tile levels
				this.map.jumpTo({ center: this.bounds.getCenter(), zoom: this.mapZoom - 1 });
			} else {
				this.map.fitBounds(this.bounds, { padding: 40, maxZoom: 14, animate: false });
			}
		}
		renderMarker(location) {
			const [width, height] = this.pinSize();
			const pin = document.createElement('img');
			pin.src = this.config.marker_image;
			pin.alt = '';
			pin.width = width;
			pin.height = height;
			pin.className = 'osm-map--pin';

			const lngLat = [location.longitude, location.latitude];
			new maplibregl.Marker({ element: pin, anchor: 'bottom' }).setLngLat(lngLat).addTo(this.map);
			this.bounds.extend(lngLat);

			pin.addEventListener('click', () => {
				const information = this.location_informations.find((el) => el.dataset.handle === location.handle);
				if (!information) {
					return;
				}
				this.closeOthers(information);
				if (!information.details.open) {
					information.details.style.overflow = 'hidden';
					information.open();
				}
				this.scrollListTo(information);
			});
		}
		scrollListTo(information) {
			const list = this.querySelector('.google-map--information--inner');
			if (!list) {
				return;
			}
			// Wait for the rows to finish opening/closing (400ms in collapsible-row), otherwise a row
			// collapsing above this one shifts it up after the position was measured.
			clearTimeout(this.scrollTimeout);
			this.scrollTimeout = setTimeout(() => {
				const offset = information.getBoundingClientRect().top - list.getBoundingClientRect().top;
				// Leave some room above the row so it isn't hidden under the list's top fade
				list.scrollTo({ top: list.scrollTop + offset - 30, behavior: 'smooth' });
			}, 450);
		}
		closeOthers(current) {
			this.location_informations.forEach((el) => {
				if (el !== current && el.details && el.details.open) {
					el.details.style.overflow = 'hidden';
					el.shrink();
				}
			});
		}
		scrollToPin(location) {
			this.map.flyTo({ center: [location.longitude, location.latitude], zoom: Math.max(this.map.getZoom(), 13), duration: 600 });
		}
	}

	const defineOsmMap = () => customElements.define('osm-map', OsmMap);

	if (window.maplibregl) {
		defineOsmMap();
	} else {
		window.addEventListener('DOMContentLoaded', defineOsmMap);
	}
}
